import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/lib/networkError';

export interface Profile {
  id: string;
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  notification_email: boolean;
  notification_push: boolean;
  notification_timer_complete: boolean;
  notification_break_over: boolean;
  notification_task_due: boolean;
  onboarding_completed: boolean;
  /** Current onboarding step (1-5). Resumed when user returns. Null if not set. */
  onboarding_step: number | null;
  primary_focus: string | null;
  tour_step: number;
  tour_completed: boolean;
  created_at: string;
  updated_at: string;
}

export function useProfile() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    if (!user) {
      setProfile(null);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      setProfile(data as Profile | null);
    } catch (err) {
      console.error('Error fetching profile:', err);
      toast({
        title: 'Error loading profile',
        description: getErrorMessage(err, 'Please try again.'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const updateProfile = async (updates: Partial<Pick<Profile, 'display_name' | 'avatar_url' | 'notification_email' | 'notification_push' | 'notification_timer_complete' | 'notification_break_over' | 'notification_task_due' | 'onboarding_completed' | 'onboarding_step' | 'primary_focus' | 'tour_step' | 'tour_completed'>>) => {
    if (!user) return { error: new Error('Not authenticated') };

    try {
      // Check if profile exists
      if (!profile) {
        // Create profile if it doesn't exist
        const { error: insertError } = await supabase
          .from('profiles')
          .insert({
            user_id: user.id,
            ...updates,
          });

        if (insertError) throw insertError;
      } else {
        // Update existing profile
        const { error: updateError } = await supabase
          .from('profiles')
          .update(updates)
          .eq('user_id', user.id);

        if (updateError) throw updateError;
      }

      await fetchProfile();
      return { error: null };
    } catch (err) {
      console.error('Error updating profile:', err);
      toast({
        title: 'Error saving profile',
        description: getErrorMessage(err, 'Please try again.'),
        variant: 'destructive',
      });
      return { error: err as Error };
    }
  };

  return {
    profile,
    loading,
    updateProfile,
    refetch: fetchProfile,
  };
}
